/**
 * Single source of truth mapping the BFF menu's stable identifiers to THIS app's
 * registered Angular routes.
 *
 * KEEP IN SYNC with src/app/app.routes.ts. `menu-navigation.service.spec.ts`
 * asserts that every route below exists as a top-level path in the router config,
 * so a typo or a removed route fails the build rather than silently degrading to
 * an external redirect.
 *
 */

/** Menu ids that are pure parent/expander nodes — never navigate, only toggle. */
export const GROUP_MENU_IDS: ReadonlySet<string> = new Set<string>([
  "SA_MNPOLICIES",
  "SAT_MT_MOTOR",
  "SA_NONMOTOR",
]);

/** BFF permission that exposes the Administrator module entry. */
export const ADMINISTRATOR_MENU_ACCESS = "SAT_ADMIN_ALERT";

/** Stable BFF `menuId` -> internal route (and optional external switch key). */
export const MENU_NAV_REGISTRY: Record<
  string,
  { route?: string; switchKey?: string }
> = {
  MNDASHBRD: { route: "/home" },
  SA_POLSUMMARY: { route: "/summary" },
  SA_POLREPORTS: { route: "/reports" },
  SA_POLENQUIRY: { route: "/enquiry" },
  SA_POLCI: { route: "/ci" },
  MNSALESMNGT: { route: "/sales-management" },
  MNCUSTMNGT: { route: "/customer-management" },
  MNELIBRARY: { route: "/e-library" },
};

/**
 * Symbolic `link` value (normalized) -> internal route. Fallback for leaves whose
 * `menuId` is not enumerated above (e.g. the Policy-Management leaves whose BFF
 * link is "ENQUIRY" / "CI" / "SUMMARY" / "REPORTS").
 */
export const LINK_KEY_ROUTES: Record<string, string> = {
  HOME: "/home",
  SUMMARY: "/summary",
  REPORTS: "/reports",
  ENQUIRY: "/enquiry",
  CI: "/ci",
  "SALES-MANAGEMENT": "/sales-management",
  "CUSTOMER-MANAGEMENT": "/customer-management",
  "E-LIBRARY": "/e-library",
  PROFILE: "/profile",
  LOGIN: "/login",
};

/**
 * "*_SYS_URL" switch key (normalized) -> internal route. Used by `navigateByKey`
 * for the admin button ("SAT_ADMIN_MENU" falls through to external) and the logo
 * ("SUMMARY_SYS_URL"). Mirrors the old `getLocalRouteForSwitchKey` map.
 */
export const SWITCH_KEY_ROUTES: Record<string, string> = {
  SAT_SYS_URL: "/home",
  SUMMARY_SYS_URL: "/summary",
  ENQUIRY_SYS_URL: "/enquiry",
  CI_SYS_URL: "/ci",
  SAT_ELIBRARY_URL: "/e-library",
  SAT_SALES_MANAGEMENT_URL: "/sales-management",
  SAT_CUSTOMER_MANAGEMENT_URL: "/customer-management",
  SAT_ESUBMISSION_URL: "/e-submission/submissions",
  SAT_PROFILE_URL: "/profile",
  LOGIN_SYS_URL: "/login",
};

/**
 * SAT module-owner `menuId` -> external switch key. Faithful port of the old
 * `getSATRedirect` bias used when deriving an external key for a SAT-owned "M"
 * item that is not internally routable. (All ids here are internally routable
 * today, so this only guards hypothetical un-registered SAT modules.)
 */
export const SAT_MODULE_SWITCH_KEYS: Record<string, string> = {
  MNDASHBRD: "SAT_SYS_URL",
  MNELIBRARY: "SAT_ELIBRARY_URL",
  MNSALESMNGT: "SAT_SALES_MANAGEMENT_URL",
  MNCUSTMNGT: "SAT_CUSTOMER_MANAGEMENT_URL",
  MNESUBMISSION: "SAT_ESUBMISSION_URL",
};

/** Strip leading/trailing slashes and uppercase, so "/enquiry/", "enquiry" and "ENQUIRY" all match. */
export const normalizeKey = (value?: string): string =>
  (value ?? "").trim().replace(/^\/+/, "").replace(/\/+$/, "").toUpperCase();
